// Async Supabase-backed store. Keeps the same shape callers expect,
// but every mutation now writes to the database under the signed-in user.
import { supabase } from '@/integrations/supabase/client';
import { Child, Domain, Milestone, AgeBand } from './types';

export interface Completion {
  milestoneId: string;
  completedAt: string;
}

interface State {
  userId: string | null;
  child: Child | null;
  completions: Completion[];
  notes: Record<string, string>;
  milestones: Milestone[];
  bands: AgeBand[];
  catalogLoaded: boolean;
  loaded: boolean;
}

const state: State = {
  userId: null,
  child: null,
  completions: [],
  notes: {},
  milestones: [],
  bands: [],
  catalogLoaded: false,
  loaded: false,
};

const emit = () => window.dispatchEvent(new Event('pebble-update'));

const BAND_DEFS: { id: string; label: string; monthMin: number; monthMax: number }[] = [
  { id: 'b-2m',  label: '2 months',  monthMin: 0,  monthMax: 2 },
  { id: 'b-6m',  label: '6 months',  monthMin: 3,  monthMax: 6 },
  { id: 'b-12m', label: '1 year',    monthMin: 7,  monthMax: 12 },
  { id: 'b-18m', label: '18 months', monthMin: 13, monthMax: 18 },
  { id: 'b-2y',  label: '2 years',   monthMin: 19, monthMax: 24 },
  { id: 'b-3y',  label: '3 years',   monthMin: 25, monthMax: 36 },
  { id: 'b-4y',  label: '4 years',   monthMin: 37, monthMax: 48 },
  { id: 'b-5y',  label: '5 years',   monthMin: 49, monthMax: 60 },
];

async function loadCatalog() {
  if (state.catalogLoaded) return;
  const { data, error } = await supabase
    .from('milestones')
    .select('id, age_band, age_months, domain, xp, title, description, science_text, source_label, source_url')
    .order('sort_order', { ascending: true });
  if (error || !data) return;

  state.milestones = data.map(r => ({
    id: r.id,
    title: r.title,
    description: r.description,
    domain: r.domain as Domain,
    ageMonths: r.age_months,
    xp: r.xp,
    science: { source: r.source_label, quote: r.science_text, url: r.source_url },
  }));

  state.bands = BAND_DEFS.map(b => ({
    ...b,
    milestones: state.milestones.filter(m => {
      const row = data.find(d => d.id === m.id);
      return row?.age_band === b.id;
    }),
  }));
  state.catalogLoaded = true;
}

async function loadAll() {
  await loadCatalog();
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user ?? null;
  state.userId = user?.id ?? null;
  if (!user) {
    state.child = null;
    state.completions = [];
    state.notes = {};
    state.loaded = true;
    emit();
    return;
  }

  const { data: children } = await supabase
    .from('children')
    .select('id, name, birthdate, priority_domains')
    .eq('user_id', user.id)
    .order('created_at', { ascending: true })
    .limit(1);

  const child = children?.[0] ?? null;
  state.child = child
    ? { id: child.id, name: child.name, birthdate: child.birthdate, focuses: (child.priority_domains ?? []) as Domain[] }
    : null;

  if (child) {
    const [{ data: comps }, { data: notes }] = await Promise.all([
      supabase.from('completions').select('milestone_id, completed_at').eq('child_id', child.id),
      supabase.from('notes').select('milestone_id, body').eq('child_id', child.id),
    ]);
    state.completions = (comps ?? []).map(c => ({ milestoneId: c.milestone_id, completedAt: c.completed_at }));
    state.notes = Object.fromEntries((notes ?? []).map(n => [n.milestone_id, n.body]));
  } else {
    state.completions = [];
    state.notes = {};
  }
  state.loaded = true;
  emit();
}

// Migrate any localStorage data from the pre-auth era (one-time, on first load).
async function migrateLocal() {
  if (!state.userId) return;
  const childRaw = localStorage.getItem('pebble.child');
  if (!childRaw) return;
  if (state.child) {
    localStorage.removeItem('pebble.child');
    localStorage.removeItem('pebble.completions');
    localStorage.removeItem('pebble.notes');
    return;
  }
  try {
    const local: Child = JSON.parse(childRaw);
    const { data: inserted, error } = await supabase
      .from('children')
      .insert({
        user_id: state.userId,
        name: local.name,
        birthdate: local.birthdate,
        priority_domains: local.focuses ?? [],
      })
      .select('id, name, birthdate, priority_domains')
      .single();
    if (error || !inserted) return;
    state.child = {
      id: inserted.id,
      name: inserted.name,
      birthdate: inserted.birthdate,
      focuses: (inserted.priority_domains ?? []) as Domain[],
    };

    const compsRaw = localStorage.getItem('pebble.completions');
    if (compsRaw) {
      const comps: Completion[] = JSON.parse(compsRaw);
      if (comps.length) {
        await supabase.from('completions').insert(
          comps.map(c => ({
            user_id: state.userId!,
            child_id: inserted.id,
            milestone_id: c.milestoneId,
            completed_at: c.completedAt,
          }))
        );
        state.completions = comps;
      }
    }
    const notesRaw = localStorage.getItem('pebble.notes');
    if (notesRaw) {
      const map: Record<string, string> = JSON.parse(notesRaw);
      const entries = Object.entries(map).filter(([, v]) => v && v.trim());
      if (entries.length) {
        await supabase.from('notes').insert(
          entries.map(([milestone_id, body]) => ({
            user_id: state.userId!,
            child_id: inserted.id,
            milestone_id,
            body,
          }))
        );
        state.notes = Object.fromEntries(entries);
      }
    }
    localStorage.removeItem('pebble.child');
    localStorage.removeItem('pebble.completions');
    localStorage.removeItem('pebble.notes');
    emit();
  } catch (e) {
    console.error('local migration failed', e);
  }
}

export async function initStore() {
  await loadAll();
  await migrateLocal();
  supabase.auth.onAuthStateChange(() => {
    setTimeout(() => { loadAll(); }, 0);
  });
}

export const store = {
  isLoaded() { return state.loaded; },
  getUserId() { return state.userId; },
  getChild(): Child | null { return state.child; },

  // Catalog
  getMilestones(): Milestone[] { return state.milestones; },
  getBands(): AgeBand[] { return state.bands; },
  findMilestone(id: string): Milestone | undefined {
    return state.milestones.find(m => m.id === id);
  },
  bandForMonths(months: number): AgeBand | undefined {
    return state.bands.find(b => months >= b.monthMin && months <= b.monthMax)
      ?? state.bands[state.bands.length - 1];
  },

  async setChild(c: { name: string; birthdate: string; focuses?: Domain[] }) {
    if (!state.userId) return;
    const focuses = c.focuses ?? [];
    if (state.child) {
      const { data, error } = await supabase
        .from('children')
        .update({ name: c.name, birthdate: c.birthdate, priority_domains: focuses })
        .eq('id', state.child.id)
        .select('id, name, birthdate, priority_domains')
        .single();
      if (!error && data) state.child = {
        id: data.id, name: data.name, birthdate: data.birthdate,
        focuses: (data.priority_domains ?? []) as Domain[],
      };
    } else {
      const { data, error } = await supabase
        .from('children')
        .insert({ user_id: state.userId, name: c.name, birthdate: c.birthdate, priority_domains: focuses })
        .select('id, name, birthdate, priority_domains')
        .single();
      if (!error && data) state.child = {
        id: data.id, name: data.name, birthdate: data.birthdate,
        focuses: (data.priority_domains ?? []) as Domain[],
      };
    }
    emit();
  },

  getCompletions(): Completion[] { return state.completions; },
  isCompleted(id: string) { return state.completions.some(c => c.milestoneId === id); },

  async toggleCompletion(id: string): Promise<boolean> {
    if (!state.userId || !state.child) return false;
    const existing = state.completions.findIndex(c => c.milestoneId === id);
    if (existing >= 0) {
      const removed = state.completions[existing];
      state.completions = state.completions.filter((_, i) => i !== existing);
      emit();
      const { error } = await supabase
        .from('completions')
        .delete()
        .eq('child_id', state.child.id)
        .eq('milestone_id', id);
      if (error) {
        state.completions = [...state.completions, removed];
        emit();
      }
      return false;
    } else {
      const newC: Completion = { milestoneId: id, completedAt: new Date().toISOString() };
      state.completions = [...state.completions, newC];
      emit();
      const { error } = await supabase.from('completions').insert({
        user_id: state.userId,
        child_id: state.child.id,
        milestone_id: id,
        completed_at: newC.completedAt,
      });
      if (error) {
        state.completions = state.completions.filter(c => c.milestoneId !== id);
        emit();
        return false;
      }
      // Streak fields on profiles are recomputed server-side by a DB trigger
      // on the completions table — no client-side update required (and the
      // streak_count / last_active_date columns are no longer writable by
      // users for security).
      return true;
    }
  },

  totalXp(): number {
    return state.completions.reduce((sum, c) => sum + (this.findMilestone(c.milestoneId)?.xp ?? 0), 0);
  },

  getNote(id: string): string { return state.notes[id] ?? ''; },

  async setNote(id: string, text: string) {
    if (!state.userId || !state.child) return;
    state.notes = { ...state.notes, [id]: text };
    emit();
    await supabase.from('notes').upsert(
      {
        user_id: state.userId,
        child_id: state.child.id,
        milestone_id: id,
        body: text,
      },
      { onConflict: 'child_id,milestone_id' }
    );
  },

  streakDays(): number {
    const days = new Set(state.completions.map(c => c.completedAt.slice(0, 10)));
    let streak = 0;
    const d = new Date();
    while (days.has(d.toISOString().slice(0, 10))) {
      streak++;
      d.setDate(d.getDate() - 1);
    }
    return streak;
  },

  async refreshCatalog() {
    state.catalogLoaded = false;
    await loadCatalog();
    emit();
  },

  async signOut() {
    await supabase.auth.signOut();
  },
};
